/**
 * 피벗 표의 **모양과 읽는 법** — 숫자는 서버가 센다(`analytics.pivot`).
 *
 * 화면은 칸 값을 읽어 그리고(`read`), 내려받을 때 네모난 값 묶음으로 편다(`toMatrix`).
 * ⚠️ 비율 지표(참여율)는 서버가 이미 퍼센트로 준다 — 여기서 다시 나누지 않는다.
 */

import type { AnalyticsMetric } from 'src/constants/pivot-fields';

export type PivotCell = { value: number; count: number };

export type PivotTable = {
  /** 행 이름 — 지표가 큰 순(날짜 축이면 시간 순) */
  rows: string[];
  /** 열 이름 — 열 축이 없으면 `['전체']` */
  columns: string[];
  /** `cells[행][열]` */
  cells: Record<string, Record<string, PivotCell>>;
  rowTotals: Record<string, PivotCell>;
  columnTotals: Record<string, PivotCell>;
  total: PivotCell;
};

/** 열 축을 안 골랐을 때의 한 칸 이름 */
export const ALL_LABEL = '전체';

/** 칸 하나가 화면에 보이는 숫자 — 없는 칸은 0 */
export function read(cell: PivotCell | undefined, _metric: AnalyticsMetric): number {
  return cell?.value ?? 0;
}

/** 피벗 표 → 내려받기용 네모난 값 묶음(첫 줄이 머리 · 마지막 줄이 합계) */
export function toMatrix(table: PivotTable, rowLabel: string, metric: AnalyticsMetric): (string | number)[][] {
  const head = [rowLabel, ...table.columns, '합계'];
  const body = table.rows.map((row) => [
    row,
    ...table.columns.map((column) => round(read(table.cells[row]?.[column], metric))),
    round(read(table.rowTotals[row], metric)),
  ]);
  const foot = ['합계', ...table.columns.map((c) => round(read(table.columnTotals[c], metric))), round(read(table.total, metric))];
  return [head, ...body, foot];
}

function round(value: number): number {
  return Math.round(value * 100) / 100;
}
